import { useEffect, useState } from 'react';
import { type DetalhamentoMonitoramentoValue, type RegraNotificacaoValue, type TipoComparacaoMonitoramento } from '../Components';
import { getErrorMessage } from '../getErrorMessage';
import { notify } from '../notify';
import {
  addMonitoramentoMembros,
  createMonitoramento,
  getMonitoramentoDetalhe,
  getMonitoramentoMembros,
  removeMonitoramentoMembro,
  updateMonitoramento,
} from '../services';
import {
  CreateMonitoramentoCidade,
  CreateMonitoramentoProduto,
  CreateMonitoramentoRede,
  CreateMonitoramentoRegra,
  MonitoramentoDetalheDTO,
  MonitoramentoPeriodo,
  MonitoramentoRegraTipo,
  MonitoramentoTipo,
} from '../types';

const DEFAULT_TIPO_CALCULO = 'PRECO_MEDIO';

const buildRegras = (regra: RegraNotificacaoValue): CreateMonitoramentoRegra[] => {
  const regras: CreateMonitoramentoRegra[] = [];

  if (regra.menorIgualAtivo) {
    regras.push({
      tipoMetrica: 'INDICE_COMPETITIVIDADE',
      tipo: MonitoramentoRegraTipo.MENOR_IGUAL,
      valorMin: Number(regra.menorIgualValor),
      valorMax: null,
      ativo: true,
    });
  }
  if (regra.maiorIgualAtivo) {
    regras.push({
      tipoMetrica: 'INDICE_COMPETITIVIDADE',
      tipo: MonitoramentoRegraTipo.MAIOR_IGUAL,
      valorMin: null,
      valorMax: Number(regra.maiorIgualValor),
      ativo: true,
    });
  }
  if (regra.entreAtivo) {
    regras.push({
      tipoMetrica: 'INDICE_COMPETITIVIDADE',
      tipo: MonitoramentoRegraTipo.ENTRE,
      valorMin: Number(regra.entreValorMin),
      valorMax: Number(regra.entreValorMax),
      ativo: true,
    });
  }

  return regras;
};

export const buildProdutos = (detalhamento: DetalhamentoMonitoramentoValue): CreateMonitoramentoProduto[] =>
  detalhamento.produtos.map((produto, index) => ({
    gtin: produto.produtoEan,
    tipo: index === 0 ? 'REFERENCIA' : 'CONCORRENTE',
  }));

export const buildRedes = (detalhamento: DetalhamentoMonitoramentoValue): CreateMonitoramentoRede[] => {
  const redes: CreateMonitoramentoRede[] = [];
  if (detalhamento.redeReferencia) {
    redes.push({ redeNome: detalhamento.redeReferencia.redeNome, tipo: 'REFERENCIA' });
  }
  detalhamento.redesConcorrentes.forEach(rede => {
    redes.push({ redeNome: rede.redeNome, tipo: 'CONCORRENTE' });
  });
  return redes;
};

export const buildCidades = (detalhamento: DetalhamentoMonitoramentoValue): CreateMonitoramentoCidade[] => {
  if (detalhamento.localTipo === 'TODO_TERRITORIO') {
    return [];
  }

  if (detalhamento.localTipo === 'UF') {
    return detalhamento.ufs.map(uf => ({ cidadeNome: '', uf: uf.replace(/\s+/g, '_') }));
  }
  return detalhamento.cidades.map(cidade => ({ cidadeNome: cidade.cidadeNome, uf: cidade.uf }));
};

const INITIAL_DETALHAMENTO: DetalhamentoMonitoramentoValue = {
  produtos: [],
  tipoComparacao: 'PRODUTO',
  categorias: [],
  secoes: [],
  redeReferencia: null,
  redesConcorrentes: [],
  localTipo: 'CIDADE',
  cidades: [],
  ufs: [],
  periodo: MonitoramentoPeriodo.ULTIMAS_4_SEMANAS,
};

const INITIAL_REGRA_NOTIFICACAO: RegraNotificacaoValue = {
  menorIgualAtivo: false,
  maiorIgualAtivo: false,
  entreAtivo: false,
  menorIgualValor: '',
  maiorIgualValor: '',
  entreValorMin: '',
  entreValorMax: '',
};

const mapDetalheToDetalhamento = (detalhe: MonitoramentoDetalheDTO): DetalhamentoMonitoramentoValue => {
  const produtoReferencia = detalhe.produtos.find(produto => produto.tipo === 'REFERENCIA');
  const produtosConcorrentes = detalhe.produtos.filter(produto => produto.tipo === 'CONCORRENTE');
  const redeReferencia = detalhe.redes.find(rede => rede.tipo === 'REFERENCIA');
  const redesConcorrentes = detalhe.redes.filter(rede => rede.tipo === 'CONCORRENTE');

  const produtoReferenciaEntry = produtoReferencia ? [{ produtoNome: produtoReferencia.produtoNome, produtoEan: produtoReferencia.produtoEan }] : [];

  const ufEntries = detalhe.cidades.filter(cidade => !cidade.cidadeNome && cidade.uf);
  const cidadeEntries = detalhe.cidades.filter(cidade => cidade.cidadeNome);
  const localTipo = detalhe.todoTerritorio ? 'TODO_TERRITORIO' : ufEntries.length > 0 ? 'UF' : 'CIDADE';

  const tipoComparacao: TipoComparacaoMonitoramento = detalhe.categorias.length > 0 ? 'CATEGORIA' : detalhe.secoes.length > 0 ? 'SECAO' : 'PRODUTO';

  return {
    produtos:
      tipoComparacao === 'PRODUTO'
        ? [...produtoReferenciaEntry, ...produtosConcorrentes.map(produto => ({ produtoNome: produto.produtoNome, produtoEan: produto.produtoEan }))]
        : [],
    tipoComparacao,
    categorias: detalhe.categorias,
    secoes: detalhe.secoes,
    redeReferencia: redeReferencia ? { redeNome: redeReferencia.redeNome } : null,
    redesConcorrentes: redesConcorrentes.map(rede => ({ redeNome: rede.redeNome })),
    localTipo,
    cidades: cidadeEntries.map(cidade => ({ cidadeNome: cidade.cidadeNome, uf: cidade.uf })),
    ufs: ufEntries.map(cidade => cidade.uf),
    periodo: detalhe.periodo as MonitoramentoPeriodo,
  };
};

const mapDetalheToRegraNotificacao = (detalhe: MonitoramentoDetalheDTO): RegraNotificacaoValue => {
  const regrasAtivas = detalhe.regras?.filter(regra => regra.ativo) ?? [];

  return regrasAtivas.reduce((acc, regra) => {
    if (regra.tipo === MonitoramentoRegraTipo.MENOR_IGUAL) {
      return { ...acc, menorIgualAtivo: true, menorIgualValor: String(regra.valorMin ?? '') };
    }
    if (regra.tipo === MonitoramentoRegraTipo.MAIOR_IGUAL) {
      return { ...acc, maiorIgualAtivo: true, maiorIgualValor: String(regra.valorMax ?? '') };
    }
    return { ...acc, entreAtivo: true, entreValorMin: String(regra.valorMin ?? ''), entreValorMax: String(regra.valorMax ?? '') };
  }, INITIAL_REGRA_NOTIFICACAO);
};

export type UseCriarMonitoramentoFormInitialValues = {
  nomeMonitoramento?: string;
  monitoringType: MonitoramentoTipo;
  detalhamento: DetalhamentoMonitoramentoValue;
  regraNotificacao?: RegraNotificacaoValue;
  priceType: string;
  physicalChannel: boolean;
  digitalChannel: boolean;
  compartilhadoCom?: string[];
};

export type UseCriarMonitoramentoFormOptions = {
  id?: string;
  initialValues?: UseCriarMonitoramentoFormInitialValues;
};

export const mapMonitoramentoDetalheToFormInitialValues = (detalhe: MonitoramentoDetalheDTO): UseCriarMonitoramentoFormInitialValues => ({
  nomeMonitoramento: detalhe.nome,
  monitoringType: detalhe.tipo,
  detalhamento: mapDetalheToDetalhamento(detalhe),
  regraNotificacao: mapDetalheToRegraNotificacao(detalhe),
  priceType: detalhe.tipoPreco,
  physicalChannel: detalhe.canalFisico,
  digitalChannel: detalhe.canalDigital,
  compartilhadoCom: detalhe.membros ?? [],
});

export const useCriarMonitoramentoForm = ({ id, initialValues }: UseCriarMonitoramentoFormOptions = {}) => {
  const isEditMode = !!id;

  const [nomeMonitoramento, setNomeMonitoramento] = useState(initialValues?.nomeMonitoramento ?? '');
  const [monitoringType, setMonitoringType] = useState<string | null>(initialValues?.monitoringType ?? null);
  const [detalhamento, setDetalhamento] = useState<DetalhamentoMonitoramentoValue>(initialValues?.detalhamento ?? INITIAL_DETALHAMENTO);
  const [regraNotificacao, setRegraNotificacao] = useState<RegraNotificacaoValue>(initialValues?.regraNotificacao ?? INITIAL_REGRA_NOTIFICACAO);
  const [priceType, setPriceType] = useState(initialValues?.priceType ?? 'PAGO');
  const [physicalChannel, setPhysicalChannel] = useState(initialValues?.physicalChannel ?? true);
  const [digitalChannel, setDigitalChannel] = useState(initialValues?.digitalChannel ?? true);
  const [compartilhadoCom, setCompartilhadoComState] = useState<string[]>(initialValues?.compartilhadoCom ?? []);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingDetalhe, setIsLoadingDetalhe] = useState(isEditMode);

  useEffect(() => {
    if (!id) return;

    setIsLoadingDetalhe(true);
    getMonitoramentoDetalhe(Number(id))
      .then(({ data: detalhe }) => {
        setNomeMonitoramento(detalhe.nome);
        setMonitoringType(detalhe.tipo);
        setDetalhamento(mapDetalheToDetalhamento(detalhe));
        setRegraNotificacao(mapDetalheToRegraNotificacao(detalhe));
        setPriceType(detalhe.tipoPreco);
        setPhysicalChannel(detalhe.canalFisico);
        setDigitalChannel(detalhe.canalDigital);
      })
      .catch(() => {
        notify('error', 5000, 'Não foi possível carregar os dados desse monitoramento.');
      })
      .finally(() => {
        setIsLoadingDetalhe(false);
      });
    getMonitoramentoMembros(Number(id))
      .then(({ data: membros }) => {
        setCompartilhadoComState(membros.map(membro => membro.email));
      })
      .catch(() => {
        setCompartilhadoComState([]);
      });
  }, [id]);

  const setCompartilhadoCom = async (nextValue: string[]) => {
    if (!isEditMode) {
      setCompartilhadoComState(nextValue);
      return;
    }

    const added = nextValue.filter(email => !compartilhadoCom.includes(email));
    const removed = compartilhadoCom.filter(email => !nextValue.includes(email));
    const previousValue = compartilhadoCom;
    setCompartilhadoComState(nextValue);

    try {
      if (added.length > 0) {
        await addMonitoramentoMembros(Number(id), added);
      }
      if (removed.length > 0) {
        await Promise.all(removed.map(email => removeMonitoramentoMembro(Number(id), email)));
      }
      notify('success', 5000, 'Compartilhamento atualizado.');
    } catch (error) {
      notify('error', 5000, getErrorMessage(error, 'Não foi possível atualizar o compartilhamento desse monitoramento.'));
      setCompartilhadoComState(previousValue);
    }
  };

  const handleSubmit = async (): Promise<MonitoramentoDetalheDTO | undefined> => {
    if (!monitoringType) {
      notify('error', 4000, 'Selecione o tipo de monitoramento.');
      return undefined;
    }

    if (detalhamento.tipoComparacao === 'CATEGORIA' && detalhamento.categorias.length === 0) {
      notify('error', 4000, 'Selecione ao menos uma categoria no Detalhamento do monitoramento.');
      return undefined;
    }

    if (detalhamento.tipoComparacao === 'SECAO' && detalhamento.secoes.length === 0) {
      notify('error', 4000, 'Selecione ao menos uma seção no Detalhamento do monitoramento.');
      return undefined;
    }

    if (detalhamento.tipoComparacao === 'PRODUTO' && detalhamento.produtos.length === 0) {
      notify('error', 4000, 'Selecione ao menos um produto no Detalhamento do monitoramento.');
      return undefined;
    }

    if (!detalhamento.redeReferencia) {
      notify('error', 4000, 'Selecione a rede referência no Detalhamento do monitoramento.');
      return undefined;
    }

    const algumaRegraAtiva = regraNotificacao.menorIgualAtivo || regraNotificacao.maiorIgualAtivo || regraNotificacao.entreAtivo;
    if (!algumaRegraAtiva) {
      notify('error', 4000, 'Selecione ao menos uma regra de notificação.');
      return undefined;
    }

    const isValorInvalido = (valor: string) => valor === '' || Number(valor) === 0;

    if (regraNotificacao.menorIgualAtivo && isValorInvalido(regraNotificacao.menorIgualValor)) {
      notify('error', 4000, 'Informe o valor da regra de notificação.');
      return undefined;
    }

    if (regraNotificacao.maiorIgualAtivo && isValorInvalido(regraNotificacao.maiorIgualValor)) {
      notify('error', 4000, 'Informe o valor da regra de notificação.');
      return undefined;
    }

    if (regraNotificacao.entreAtivo && (isValorInvalido(regraNotificacao.entreValorMin) || isValorInvalido(regraNotificacao.entreValorMax))) {
      notify('error', 4000, 'Informe os valores mínimo e máximo da regra de notificação.');
      return undefined;
    }

    setIsSubmitting(true);
    try {
      const body = {
        nome: nomeMonitoramento,
        tipo: monitoringType as MonitoramentoTipo,
        periodo: detalhamento.periodo,
        todoTerritorio: detalhamento.localTipo === 'TODO_TERRITORIO',
        tipoPreco: priceType,
        tipoCalculo: DEFAULT_TIPO_CALCULO,
        canalFisico: physicalChannel,
        canalDigital: digitalChannel,
        notificarEmail: true,
        notificarCentral: true,
        produtos: buildProdutos(detalhamento),
        redes: buildRedes(detalhamento),
        cidades: buildCidades(detalhamento),
        regras: buildRegras(regraNotificacao),
        categorias: detalhamento.tipoComparacao === 'CATEGORIA' ? detalhamento.categorias : [],
        secoes: detalhamento.tipoComparacao === 'SECAO' ? detalhamento.secoes : [],
        membros: compartilhadoCom,
      };

      const { data } = isEditMode ? await updateMonitoramento(Number(id), body) : await createMonitoramento(body);
      notify('success', 5000, isEditMode ? 'Monitoramento atualizado com sucesso.' : 'Monitoramento criado com sucesso.');
      return data;
    } catch (error) {
      notify('error', 5000, getErrorMessage(error, isEditMode ? 'Não foi possível atualizar o monitoramento.' : 'Não foi possível criar o monitoramento.'));
      return undefined;
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    isEditMode,
    nomeMonitoramento,
    setNomeMonitoramento,
    monitoringType,
    setMonitoringType,
    detalhamento,
    setDetalhamento,
    regraNotificacao,
    setRegraNotificacao,
    priceType,
    setPriceType,
    physicalChannel,
    setPhysicalChannel,
    digitalChannel,
    setDigitalChannel,
    compartilhadoCom,
    setCompartilhadoCom,
    isSubmitting,
    isLoadingDetalhe,
    handleSubmit,
  };
};
